import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ContentLocalesI } from '~src/interfaces/session.types';
import { Topic } from '~src/interfaces/topic.interface';
import Checkbox, { CheckBoxStatus, CheckBoxType } from '~src/shared/form/Checkbox';
import { FieldType } from '~src/types';
import {
  HeaderContainer,
  TopicDescription,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';
import { translateContent } from '~src/services/translationServices';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import CaretBtn from '~src/shared/buttons/caretBtn/CaretBtn';
import { Question } from '~src/interfaces/question.interface';
import ListEditForm from '~src/modules/surveys/components/topic_select/ListEditForm';

import {
  AllQuestionsContainer,
  CaretContainer,
  CheckBoxContainer,
  QuestionContainer,
  ToggleTopic,
  TopicListContainer,
  TopicListHeader,
  TopicName,
} from './topicQuestions.sc';
import QuestionItem from './QuestionItem';

interface Props {
  topic: Topic;
  selectAll: (topic: Topic, value: boolean) => Promise<void>;
  selectedQuestions: Record<string, boolean>;
  mandatoryQuestions: string[];
  defaultContentLocale: string;
  isCreated: boolean;
  changeShowSave: (value: boolean) => void;
  renderAddForm: () => JSX.Element | null;
  onEdit?: (value: string | null, question: Question) => Promise<{}>;
  onDelete?: (questionId: string, name: ContentLocalesI | null) => Promise<{}>;
}

const TopicSection: FC<Props> = (props) => {
  const [showQuestions, setShowQuestions] = useState(props.isCreated);
  const [checkBoxState, setCheckBoxState] = useState(CheckBoxStatus.Empty);
  const [editQuestion, setEditQuestion] = useState<string | null>(null);
  const [t] = useTranslation();

  useEffect(() => {
    setCheckBoxType(props.selectedQuestions);
  }, [props.selectedQuestions]);

  const [isCreated, setIsCreated] = useState(false);

  useEffect(() => {
    if (props.isCreated && !isCreated) {
      setShowQuestions(true);
      setIsCreated(props.isCreated);
    }
  }, [props.isCreated]);

  function isAnyQuestionSelected(): boolean {
    if (props.selectedQuestions) {
      return Object.values(props.selectedQuestions).some((v) => v);
    }
    return false;
  }

  function setCheckBoxType(questionsList: { [key: string]: boolean }) {
    if (questionsList && Object.values(questionsList).length > 0) {
      const selectedList = Object.values(questionsList);
      const someSelected = selectedList.some((v) => v);
      const allSelected = selectedList.every((v) => v);
      if (!someSelected) {
        setCheckBoxState(CheckBoxStatus.Empty);
      }
      if (someSelected && !allSelected) {
        setShowQuestions(true);
        setCheckBoxState(CheckBoxStatus.Partial);
      }
      if (allSelected && questionsList) {
        setShowQuestions(true);
        setCheckBoxState(CheckBoxStatus.Checked);
      }
    }
  }

  async function onClickTopicCheckbox(e: SyntheticEvent) {
    if (Object.keys(props.selectedQuestions).length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setShowQuestions(true);
      if (checkBoxState !== CheckBoxStatus.Empty) {
        setCheckBoxState(CheckBoxStatus.Empty);
      } else {
        setCheckBoxState(CheckBoxStatus.Checked);
      }

      await props.selectAll(props.topic, !isAnyQuestionSelected());
    }
  }

  function editThisQuestion(questionId: string, e: SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();
    setEditQuestion(questionId);
  }

  function resetQuestionEdit() {
    setEditQuestion(null);
  }

  function questionCheckBoxClicked(event: React.ChangeEvent<HTMLDivElement> | undefined, newValue: boolean) {
    if (event) {
      if (!event.target.id) {
        throw Error('Checkbox needs prop id');
      } else {
        const updatedList = {
          ...props.selectedQuestions,
          [event.target.id]: newValue,
        };
        setCheckBoxType(updatedList);
        props.changeShowSave(true);
      }
    }
  }

  const renderField: FC<FieldType & { question: Question }> = (
    inputProps: FieldType & { question: Question },
  ) => (
    <QuestionContainer>
      <QuestionItem
        input={inputProps.input}
        question={inputProps.question}
        editThisQuestion={editThisQuestion}
        onEdit={props.onEdit}
        onChange={() => {
          inputProps.input.onChange(!inputProps.input.checked);
          props.changeShowSave(true);
        }}
      />
    </QuestionContainer>
  );

  const renderMandatoryButton: FC<FieldType & { question: Question }> = ({
    input,
  }: FieldType & { question: Question }) => {
    return (
      <div
        id={input.id}
        style={{ marginLeft: 'auto', marginRight: 30, opacity: input.checked ? 1 : 0.3 }}
        onClick={() => {
          props.changeShowSave(true);
          input.onChange(!input.checked);
        }}
      >
        <Checkbox
          type={CheckBoxType.CheckBox}
          id={input.id}
          onChange={input.onChange}
          isChecked={input.checked}
          input={input}
          flipLabel
          label={t('x.surveys.details.select_question.mandatory')}
        />
      </div>
    );
  };

  function toggleQuestions() {
    setShowQuestions(!showQuestions);
  }

  return (
    <TopicListContainer $open={showQuestions || !props.topic.questions}>
      <TopicListHeader onClick={toggleQuestions}>
        <ToggleTopic onClick={onClickTopicCheckbox}>
          <CheckBoxContainer>
            <Checkbox
              id={props.topic.id}
              emptyDisabled={!props.topic.questions}
              val={props.topic.id}
              isPartial={checkBoxState === 'partial'}
              isChecked={checkBoxState !== 'empty'}
            />
          </CheckBoxContainer>
          <TopicName>
            <HeaderContainer>
              <span>{translateContent(props.topic.name)}</span>
              <TopicDescription>
                {props.topic.description && translateContent(props.topic.description) && (
                  <HoverInfoIcon infoText={translateContent(props.topic.description)} />
                )}
              </TopicDescription>
            </HeaderContainer>
          </TopicName>
        </ToggleTopic>
        {props.topic.questions && (
          <CaretContainer>
            <CaretBtn withContainer size="18px" open={showQuestions} />
          </CaretContainer>
        )}
      </TopicListHeader>
      {showQuestions && (
        <AllQuestionsContainer $show={showQuestions || props.topic.questions}>
          {editQuestion && props.topic.questions ? (
            <ListEditForm
              resetQuestionEdit={resetQuestionEdit}
              onEdit={props.onEdit}
              onDelete={props.onDelete}
              topic={props.topic}
              editQuestion={editQuestion}
            />
          ) : (
            <>
              {props.topic.questions &&
                props.topic.questions.map((question: Question) => (
                  <div style={{ display: 'flex', paddingTop: 4, paddingBottom: 4 }} key={question.id}>
                    <Field
                      key={`questions.${question.topicId}.${question.id}`}
                      name={`questions.${question.topicId}.${question.id}`}
                      type="checkbox"
                      component={renderField}
                      question={question}
                      onChange={questionCheckBoxClicked}
                    />
                    {props.selectedQuestions[question.id] && (
                      <Field
                        id={`mandatory.${question.id}`}
                        key={`mandatory.${question.id}`}
                        name={`mandatory.${question.id}`}
                        type="checkbox"
                        question={question}
                        component={renderMandatoryButton}
                        onChange={questionCheckBoxClicked}
                      />
                    )}
                  </div>
                ))}
              <div style={{display: "flex", alignItems: "center"}}>
              {props.renderAddForm()}
              </div>
            </>
          )}
        </AllQuestionsContainer>
      )}
    </TopicListContainer>
  );
};

export default React.memo(TopicSection);
